/**
 * BackgroundEditor - Modal for editing scene background with layers and animations
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useSceneStore } from '../../../stores/sceneStore';
import { useModalContext } from '../../../contexts/ModalContext';
import { useApiClient } from '../../../api/client';
import MediaPicker from '../../../modals/MediaPicker';
import type { 
  Background, 
  ParallaxLayer, 
  BackgroundLayerAnimation,
  TranslateDirection,
  Position,
  Transform,
  ResponsiveLayout
} from '../../../../../Shared/src/@types';
import { BACKGROUND_NAME_MAX } from '../../../../../Shared/src/operations';
import { backgroundDefaultState, hasBackgroundStates } from '../../../../../Shared/src/utils/backgroundStates';
import styles from './BackgroundEditor.module.scss';

// Generate unique IDs
const generateId = () => Math.random().toString(36).substring(2, 9);

// Direction options for translate
const DIRECTIONS: { value: TranslateDirection; label: string; icon: string }[] = [
  { value: 'up-left', label: 'Up-Left', icon: '↖' },
  { value: 'up', label: 'Up', icon: '↑' },
  { value: 'up-right', label: 'Up-Right', icon: '↗' },
  { value: 'left', label: 'Left', icon: '←' },
  { value: 'right', label: 'Right', icon: '→' },
  { value: 'down-left', label: 'Down-Left', icon: '↙' },
  { value: 'down', label: 'Down', icon: '↓' },
  { value: 'down-right', label: 'Down-Right', icon: '↘' },
];

// Easing options
const EASING_OPTIONS = [
  { value: 'power1.out', label: 'Ease Out' },
  { value: 'power1.in', label: 'Ease In' },
  { value: 'power1.inOut', label: 'Ease In-Out' },
  { value: 'power2.out', label: 'Ease Out (Strong)' },
  { value: 'power2.inOut', label: 'Ease In-Out (Strong)' },
  { value: 'sine.inOut', label: 'Sine' },
  { value: 'none', label: 'Linear' },
];

// Selection id of the fixed "Base image" row (layer ids are 7-char random strings)
const BASE_ROW_ID = '__base__';

// Default layer
const createDefaultLayer = (mediaId: number = 0): ParallaxLayer => ({
  id: generateId(),
  mediaId,
  depth: 1,
  layout: {
    portrait: {
      position: { x: 0, y: 0, unit: '%', anchor: 'top-left' },
    },
  },
  transform: { scale: 1, rotation: 0 },
  opacity: 1,
  animation: {
    translate: { enabled: false, direction: 'right', speed: 50, loop: true },
    zoom: { enabled: false, scale: 0.1, duration: 2000, yoyo: true, loop: true, ease: 'power1.inOut' },
    rotate: { enabled: false, angle: 10, duration: 2000, yoyo: true, loop: true, ease: 'power1.inOut' },
  },
});

interface BackgroundEditorProps {
  onClose: () => void;
  /**
   * Spec 040 D-2: "Edit states" on a background with states. The panel
   * closes the modal and focuses its States section; without it the
   * button only closes the modal.
   */
  onEditStates?: () => void;
}

export const BackgroundEditor: React.FC<BackgroundEditorProps> = ({ onClose, onEditStates }) => {
  const { addModal, removeModal } = useModalContext();
  const apiClient = useApiClient();
  
  // Store
  const scene = useSceneStore((state) => state.scene);
  const updateBackground = useSceneStore((state) => state.updateBackground);
  
  // Local state for editing
  const [background, setBackground] = useState<Background>(() => {
    if (scene?.background) {
      return { ...scene.background };
    }
    return {
      id: generateId(),
      name: 'Background',
      layers: [],
      color: '#1a1a2e',
    };
  });
  
  // The name as it was opened: an untouched legacy name is never refused
  const [initialName] = useState(() => background.name ?? '');
  
  // Spec 040 D-2: with states, the base row shows the default state's picture read-only.
  const withStates = hasBackgroundStates(background);
  const defaultStateName = backgroundDefaultState(background);

  const [selectedLayerId, setSelectedLayerId] = useState<string | null>(
    background.layers?.[0]?.id || (background.baseMediaId || hasBackgroundStates(background) ? BASE_ROW_ID : null)
  );
  const [isPlaying, setIsPlaying] = useState(false);
  const [layerImages, setLayerImages] = useState<Record<string, HTMLImageElement>>({});
  // The base image (background.baseMediaId); its object URL stays alive for the row thumbnail
  const [baseImage, setBaseImage] = useState<{ id: number; img: HTMLImageElement; url: string } | null>(null);
  
  // Canvas refs
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const animationFrameRef = useRef<number>(0);
  const animationStateRef = useRef<Record<string, any>>({});
  const lastAnimationConfigRef = useRef<string>('');
  const startTimeRef = useRef<number>(0);
  
  // Get selected layer
  const selectedLayer = background.layers?.find(l => l.id === selectedLayerId) || null;
  const isBaseSelected = selectedLayerId === BASE_ROW_ID;
  const baseMediaId = typeof background.baseMediaId === 'number' && background.baseMediaId > 0
    ? background.baseMediaId
    : undefined;
  
  // Name rule of setBackground (spec 026): 1–BACKGROUND_NAME_MAX characters after trimming
  const trimmedName = (background.name ?? '').trim();
  const nameError = background.name !== initialName
    && (trimmedName.length < 1 || trimmedName.length > BACKGROUND_NAME_MAX)
    ? `Name must be 1–${BACKGROUND_NAME_MAX} characters`
    : null;
  
  // Load the base image (drawn cover-fit under the layers, like SceneCanvas and the runtime)
  useEffect(() => {
    if (baseMediaId === undefined || baseImage?.id === baseMediaId) return;
    let cancelled = false;
    (async () => {
      try {
        const blob = await apiClient.getMediaFileBlob(baseMediaId);
        const url = URL.createObjectURL(blob);
        const img = new Image();
        img.src = url;
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
        });
        if (cancelled) URL.revokeObjectURL(url);
        else setBaseImage({ id: baseMediaId, img, url });
      } catch (e) {
        console.error('Failed to load base image:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [baseMediaId, baseImage, apiClient]);
  
  // Revoke the base thumbnail's object URL when it is replaced or the editor closes
  useEffect(() => {
    if (!baseImage) return;
    return () => URL.revokeObjectURL(baseImage.url);
  }, [baseImage]);
  
  // Load images for layers as HTMLImageElement (for canvas drawing)
  useEffect(() => {
    const loadImages = async () => {
      if (!background.layers) return;
      
      const newImages: Record<string, HTMLImageElement> = {};
      for (const layer of background.layers) {
        if (layer.mediaId && !layerImages[layer.id]) {
          try {
            const blob = await apiClient.getMediaFileBlob(layer.mediaId);
            const url = URL.createObjectURL(blob);
            const img = new Image();
            img.src = url;
            await new Promise((resolve, reject) => {
              img.onload = resolve;
              img.onerror = reject;
            });
            newImages[layer.id] = img;
            // Revoke after image is loaded
            URL.revokeObjectURL(url);
          } catch (e) {
            console.error('Failed to load layer image:', e);
          }
        }
      }
      
      if (Object.keys(newImages).length > 0) {
        setLayerImages(prev => ({ ...prev, ...newImages }));
      }
    };
    
    loadImages();
  }, [background.layers, apiClient]);
  
  // Compute animation config hash to detect changes
  const getAnimationConfigHash = useCallback(() => {
    return JSON.stringify(background.layers?.map(l => ({
      id: l.id,
      anim: l.animation,
    })));
  }, [background.layers]);
  
  // Reset animation state when config changes (e.g., direction changed)
  useEffect(() => {
    const currentConfig = getAnimationConfigHash();
    if (lastAnimationConfigRef.current && lastAnimationConfigRef.current !== currentConfig) {
      // Animation config changed - reset state
      animationStateRef.current = {};
      startTimeRef.current = 0;
    }
    lastAnimationConfigRef.current = currentConfig;
  }, [background.layers, getAnimationConfigHash]);
  
  // Draw a single frame to canvas
  const drawFrame = useCallback((elapsed: number, deltaTime: number) => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Get container dimensions for display
    const rect = container.getBoundingClientRect();
    const displayWidth = rect.width;
    const displayHeight = rect.height;
    
    // Make canvas square based on max dimension (for rotation without blank corners)
    const size = Math.max(displayWidth, displayHeight) * Math.SQRT2; // sqrt(2) for 45-degree rotation
    
    if (canvas.width !== size || canvas.height !== size) {
      canvas.width = size;
      canvas.height = size;
    }
    
    // Clear and fill background
    ctx.fillStyle = background.color || '#1a1a2e';
    ctx.fillRect(0, 0, size, size);
    
    // Base image: cover-fit over the colour and under the layers
    if (baseMediaId !== undefined && baseImage?.id === baseMediaId) {
      const { img } = baseImage;
      if (img.width > 0 && img.height > 0) {
        const k = Math.max(size / img.width, size / img.height);
        ctx.drawImage(img, (size - img.width * k) / 2, (size - img.height * k) / 2, img.width * k, img.height * k);
      }
    }
    
    // Draw each layer
    background.layers?.forEach((layer) => {
      const img = layerImages[layer.id];
      if (!img) return;
      
      const anim = layer.animation;
      const state = animationStateRef.current[layer.id] || {
        translateOffset: { x: 0, y: 0 },
        translateComplete: false,
        zoomComplete: false,
        rotateComplete: false,
      };
      animationStateRef.current[layer.id] = state;
      
      // Calculate animation values
      let translateX = 0;
      let translateY = 0;
      let scale = 1;
      let rotate = 0;
      
      // Translate animation
      if (anim?.translate?.enabled && !state.translateComplete) {
        const speed = anim.translate.speed * (deltaTime / 1000);
        const dir = anim.translate.direction;
        
        if (dir.includes('left')) state.translateOffset.x -= speed;
        if (dir.includes('right')) state.translateOffset.x += speed;
        if (dir.includes('up')) state.translateOffset.y -= speed;
        if (dir.includes('down')) state.translateOffset.y += speed;
        
        if (anim.translate.loop) {
          // Loop: use image dimensions for seamless wrapping
          // Wrap using positive modulo to avoid negative jumps
          const wrapX = img.width;
          const wrapY = img.height;
          state.translateOffset.x = ((state.translateOffset.x % wrapX) + wrapX) % wrapX;
          state.translateOffset.y = ((state.translateOffset.y % wrapY) + wrapY) % wrapY;
        } else {
          // No loop: stop at one full canvas movement
          if (Math.abs(state.translateOffset.x) >= size || Math.abs(state.translateOffset.y) >= size) {
            state.translateOffset.x = Math.sign(state.translateOffset.x) * Math.min(Math.abs(state.translateOffset.x), size);
            state.translateOffset.y = Math.sign(state.translateOffset.y) * Math.min(Math.abs(state.translateOffset.y), size);
            state.translateComplete = true;
          }
        }
        
        translateX = state.translateOffset.x;
        translateY = state.translateOffset.y;
      } else if (anim?.translate?.enabled && state.translateComplete) {
        translateX = state.translateOffset.x;
        translateY = state.translateOffset.y;
      }
      
      // Zoom animation
      if (anim?.zoom?.enabled && !state.zoomComplete) {
        let progress = elapsed / anim.zoom.duration;
        
        if (anim.zoom.loop) {
          progress = progress % 1;
        } else if (progress >= 1) {
          progress = 1;
          state.zoomComplete = true;
        }
        
        let easedProgress = progress;
        if (anim.zoom.yoyo) {
          easedProgress = progress < 0.5 ? progress * 2 : 2 - progress * 2;
        }
        
        scale = 1 + (anim.zoom.scale * easedProgress);
      } else if (anim?.zoom?.enabled && state.zoomComplete) {
        const finalProgress = anim.zoom.yoyo ? 0 : 1;
        scale = 1 + (anim.zoom.scale * finalProgress);
      }
      
      // Rotate animation
      if (anim?.rotate?.enabled && !state.rotateComplete) {
        let progress = elapsed / anim.rotate.duration;
        
        if (anim.rotate.loop) {
          progress = progress % 1;
        } else if (progress >= 1) {
          progress = 1;
          state.rotateComplete = true;
        }
        
        let easedProgress = progress;
        if (anim.rotate.yoyo) {
          easedProgress = progress < 0.5 ? progress * 2 : 2 - progress * 2;
        }
        
        rotate = (anim.rotate.angle * easedProgress) * Math.PI / 180;
      } else if (anim?.rotate?.enabled && state.rotateComplete) {
        const finalProgress = anim.rotate.yoyo ? 0 : 1;
        rotate = (anim.rotate.angle * finalProgress) * Math.PI / 180;
      }
      
      // Apply layer opacity
      ctx.globalAlpha = layer.opacity;
      
      // Save context state
      ctx.save();
      
      // Move to center for transforms
      ctx.translate(size / 2, size / 2);
      ctx.rotate(rotate);
      ctx.scale(scale, scale);
      ctx.translate(-size / 2, -size / 2);
      
      // Draw the layer
      if (anim?.translate?.enabled && anim.translate.loop) {
        // Use pattern for seamless tiling
        const pattern = ctx.createPattern(img, 'repeat');
        if (pattern) {
          ctx.save();
          ctx.translate(translateX, translateY);
          ctx.fillStyle = pattern;
          // Draw a larger area to ensure full coverage after transforms
          ctx.fillRect(-size, -size, size * 3, size * 3);
          ctx.restore();
        }
      } else {
        // Draw single image, scaled to cover canvas
        const imgAspect = img.width / img.height;
        const canvasAspect = 1; // Square canvas
        
        let drawWidth, drawHeight, drawX, drawY;
        
        if (imgAspect > canvasAspect) {
          // Image is wider - fit to height
          drawHeight = size;
          drawWidth = size * imgAspect;
          drawX = (size - drawWidth) / 2 + translateX;
          drawY = translateY;
        } else {
          // Image is taller - fit to width
          drawWidth = size;
          drawHeight = size / imgAspect;
          drawX = translateX;
          drawY = (size - drawHeight) / 2 + translateY;
        }
        
        ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
      }
      
      // Restore context state
      ctx.restore();
      ctx.globalAlpha = 1;
    });
    
    // Draw selection indicator
    if (selectedLayerId) {
      ctx.strokeStyle = '#4a9eff';
      ctx.lineWidth = 2;
      ctx.strokeRect(1, 1, size - 2, size - 2);
    }
  }, [background.layers, background.color, baseMediaId, baseImage, layerImages, selectedLayerId]);
  
  // Animation loop
  useEffect(() => {
    if (!isPlaying) {
      // Draw one static frame when not playing
      drawFrame(0, 0);
      return;
    }
    
    startTimeRef.current = performance.now();
    let lastTime = startTimeRef.current;
    
    // Reset animation state at start
    animationStateRef.current = {};
    
    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTimeRef.current;
      const deltaTime = currentTime - lastTime;
      lastTime = currentTime;
      
      drawFrame(elapsed, deltaTime);
      
      animationFrameRef.current = requestAnimationFrame(animate);
    };
    
    animationFrameRef.current = requestAnimationFrame(animate);
    
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isPlaying, drawFrame]);
  
  // Redraw when layers or images change (static frame)
  useEffect(() => {
    if (!isPlaying) {
      drawFrame(0, 0);
    }
  }, [background.layers, layerImages, isPlaying, drawFrame]);
  
  // Add layer
  const handleAddLayer = useCallback(() => {
    addModal({
      title: 'Select Background Image',
      body: (
        <MediaPicker
          typeFilter="image"
          lockFilter
          onSelect={(mediaId: number) => {
            const newLayer = createDefaultLayer(mediaId);
            setBackground(prev => ({
              ...prev,
              layers: [...(prev.layers || []), newLayer],
            }));
            setSelectedLayerId(newLayer.id);
            removeModal();
          }}
          onCancel={() => removeModal()}
        />
      ),
      size: 'lg',
      theme: 'dark',
    });
  }, [addModal, removeModal]);
  
  // Remove layer
  const handleRemoveLayer = useCallback((layerId: string) => {
    setBackground(prev => ({
      ...prev,
      layers: prev.layers?.filter(l => l.id !== layerId) || [],
    }));
    if (selectedLayerId === layerId) {
      setSelectedLayerId(background.layers?.[0]?.id || null);
    }
    // Remove from images
    if (layerImages[layerId]) {
      setLayerImages(prev => {
        const next = { ...prev };
        delete next[layerId];
        return next;
      });
    }
  }, [selectedLayerId, background.layers, layerImages]);
  
  // Update layer
  const updateLayer = useCallback((layerId: string, updates: Partial<ParallaxLayer>) => {
    setBackground(prev => ({
      ...prev,
      layers: prev.layers?.map(l => l.id === layerId ? { ...l, ...updates } : l) || [],
    }));
  }, []);
  
  // Update layer animation
  const updateLayerAnimation = useCallback((
    layerId: string, 
    animType: 'translate' | 'zoom' | 'rotate',
    updates: any
  ) => {
    setBackground(prev => ({
      ...prev,
      layers: prev.layers?.map(l => {
        if (l.id !== layerId) return l;
        return {
          ...l,
          animation: {
            ...l.animation,
            [animType]: {
              ...(l.animation?.[animType] || {}),
              ...updates,
            },
          },
        };
      }) || [],
    }));
  }, []);
  
  // Change layer image
  const handleChangeLayerImage = useCallback((layerId: string) => {
    addModal({
      title: 'Select Background Image',
      body: (
        <MediaPicker
          typeFilter="image"
          lockFilter
          onSelect={async (mediaId: number) => {
            updateLayer(layerId, { mediaId });
            // Load new image as HTMLImageElement
            try {
              const blob = await apiClient.getMediaFileBlob(mediaId);
              const url = URL.createObjectURL(blob);
              const img = new Image();
              img.src = url;
              await new Promise((resolve, reject) => {
                img.onload = resolve;
                img.onerror = reject;
              });
              setLayerImages(prev => ({ ...prev, [layerId]: img }));
              URL.revokeObjectURL(url);
            } catch (e) {
              console.error('Failed to load image:', e);
            }
            removeModal();
          }}
          onCancel={() => removeModal()}
        />
      ),
      size: 'lg',
      theme: 'dark',
    });
  }, [addModal, removeModal, updateLayer, apiClient]);
  
  // Swap the base image
  const handleSwapBaseImage = useCallback(() => {
    addModal({
      title: 'Select Background Image',
      body: (
        <MediaPicker
          typeFilter="image"
          lockFilter
          onSelect={(mediaId: number) => {
            setBackground(prev => ({ ...prev, baseMediaId: mediaId }));
            setSelectedLayerId(BASE_ROW_ID);
            removeModal();
          }}
          onCancel={() => removeModal()}
        />
      ),
      size: 'lg',
      theme: 'dark',
    });
  }, [addModal, removeModal]);
  
  // Remove the base image; the name stays, so a named background falls back to its placeholder
  const handleRemoveBaseImage = useCallback(() => {
    setBackground(prev => {
      const { baseMediaId: _removed, ...rest } = prev;
      return rest;
    });
  }, []);
  
  // Save and close (baseMediaId always passed, so a removed one is deleted from the scene)
  const handleSave = useCallback(() => {
    if (nameError) return;
    const name = background.name === initialName ? background.name : trimmedName;
    updateBackground({ ...background, name, baseMediaId: background.baseMediaId });
    onClose();
  }, [background, initialName, trimmedName, nameError, updateBackground, onClose]);

  // "Edit states" (spec 040 D-2): edits made here are kept (when the name is
  // valid and something changed), then the modal hands over to the States section.
  const handleEditStates = useCallback(() => {
    const changed = JSON.stringify(background) !== JSON.stringify(scene?.background ?? null);
    if (changed && !nameError) {
      const name = background.name === initialName ? background.name : trimmedName;
      updateBackground({ ...background, name, baseMediaId: background.baseMediaId });
    }
    if (onEditStates) onEditStates();
    else onClose();
  }, [background, scene, initialName, trimmedName, nameError, updateBackground, onEditStates, onClose]);
  
  // Toggle play/pause
  const handleTogglePlay = useCallback(() => {
    setIsPlaying(prev => !prev);
  }, []);
  
  return (
    <div className={styles.editor}>
      {/* Preview Area */}
      <div className={styles.previewSection}>
        <div className={styles.previewHeader}>
          <h4>Preview</h4>
          <button 
            className={`${styles.playButton} ${isPlaying ? styles.playing : ''}`}
            onClick={handleTogglePlay}
          >
            {isPlaying ? '⏸ Pause' : '▶ Play'}
          </button>
        </div>
        <div 
          className={styles.previewCanvas}
          ref={containerRef}
        >
          <canvas 
            ref={canvasRef}
            className={styles.canvas}
          />
          {(!background.layers || background.layers.length === 0) && baseMediaId === undefined && (
            <div className={styles.previewEmpty}>
              No layers. Add a background image to get started.
            </div>
          )}
        </div>
      </div>
      
      {/* Layers List */}
      <div className={styles.layersSection}>
        <div className={styles.layersHeader}>
          <h4>Layers</h4>
          <button className={styles.addLayerBtn} onClick={handleAddLayer}>
            + Add Layer
          </button>
        </div>
        <div className={styles.layersList}>
          {background.layers?.map((layer, index) => (
            <div 
              key={layer.id}
              className={`${styles.layerItem} ${selectedLayerId === layer.id ? styles.selected : ''}`}
              onClick={() => setSelectedLayerId(layer.id)}
            >
              <div 
                className={styles.layerThumb}
                style={{ backgroundImage: layerImages[layer.id] ? `url(${layerImages[layer.id]})` : undefined }}
              />
              <span className={styles.layerName}>Layer {index + 1}</span>
              <button 
                className={styles.removeLayerBtn}
                onClick={(e) => { e.stopPropagation(); handleRemoveLayer(layer.id); }}
              >
                ×
              </button>
            </div>
          ))}
          
          {/* Base image: fixed at the bottom, under every layer; not reorderable or animated */}
          <div
            className={`${styles.layerItem} ${styles.baseItem} ${isBaseSelected ? styles.selected : ''}`}
            onClick={() => setSelectedLayerId(BASE_ROW_ID)}
            data-testid="background-base-row"
          >
            <div
              className={styles.layerThumb}
              style={{ backgroundImage: baseMediaId !== undefined && baseImage?.id === baseMediaId ? `url(${baseImage.url})` : undefined }}
            />
            <span className={styles.layerName}>
              <span className={styles.baseLabel}>Base image</span>
              <span className={styles.baseMeta} data-testid="background-base-meta">
                {background.name || 'Background'}
                {withStates ? ` · Default state: ${defaultStateName}` : ''}
                {baseMediaId === undefined ? ' · No picture' : ''}
              </span>
            </span>
          </div>
        </div>
        
        {/* Background Color */}
        <div className={styles.colorField}>
          <label>Background Color</label>
          <input
            type="color"
            aria-label="Background colour"
            value={background.color || '#1a1a2e'}
            onChange={(e) => setBackground(prev => ({ ...prev, color: e.target.value }))}
          />
        </div>
      </div>
      
      {/* Base image properties */}
      {isBaseSelected && (
        <div className={styles.propertiesSection}>
          <h4>Base Image</h4>
          
          {/* Name: the place it shows; a named background without a picture plays as a placeholder (spec 026) */}
          <div className={styles.propertyGroup}>
            <label htmlFor="background-name">Name</label>
            <input
              id="background-name"
              type="text"
              className={styles.textInput}
              value={background.name ?? ''}
              maxLength={BACKGROUND_NAME_MAX}
              onChange={(e) => setBackground(prev => ({ ...prev, name: e.target.value }))}
            />
            {nameError && <p className={styles.fieldError} role="alert">{nameError}</p>}
          </div>
          
          {/* Picture */}
          {withStates ? (
            // Spec 040 D-2: the picture has one editor, the States section; here it is read-only.
            <div className={styles.propertyGroup} data-testid="background-default-state">
              <label>Picture</label>
              <p className={styles.baseEmpty} data-testid="background-default-state-label">Default state: {defaultStateName}</p>
              {baseMediaId !== undefined && baseImage?.id === baseMediaId && (
                <img src={baseImage.url} alt={`Default state: ${defaultStateName}`} className={styles.defaultStatePicture} data-testid="background-default-state-picture" />
              )}
              {baseMediaId === undefined && <p className={styles.baseEmpty}>No picture</p>}
              <button className={styles.changeImageBtn} onClick={handleEditStates}>
                Edit states
              </button>
            </div>
          ) : (
          <div className={styles.propertyGroup}>
            <label>Picture</label>
            {baseMediaId === undefined && <p className={styles.baseEmpty}>No picture</p>}
            <button className={styles.changeImageBtn} onClick={handleSwapBaseImage}>
              {baseMediaId !== undefined ? 'Swap Picture' : 'Select Picture'}
            </button>
            {baseMediaId !== undefined && (
              <button className={`${styles.changeImageBtn} ${styles.removeImageBtn}`} onClick={handleRemoveBaseImage}>
                Remove Picture
              </button>
            )}
          </div>
          )}
        </div>
      )}
      
      {/* Layer Properties */}
      {selectedLayer && (
        <div className={styles.propertiesSection}>
          <h4>Layer Properties</h4>
          
          {/* Image */}
          <div className={styles.propertyGroup}>
            <label>Image</label>
            <button 
              className={styles.changeImageBtn}
              onClick={() => handleChangeLayerImage(selectedLayer.id)}
            >
              {selectedLayer.mediaId > 0 ? 'Change Image' : 'Select Image'}
            </button>
          </div>
          
          {/* Opacity */}
          <div className={styles.propertyGroup}>
            <label>Opacity: {Math.round(selectedLayer.opacity * 100)}%</label>
            <input
              type="range"
              min={0}
              max={1}
              step={0.01}
              value={selectedLayer.opacity}
              onChange={(e) => updateLayer(selectedLayer.id, { opacity: parseFloat(e.target.value) })}
            />
          </div>
          
          {/* Translate Animation */}
          <div className={styles.animationGroup}>
            <div className={styles.animationHeader}>
              <label>
                <input
                  type="checkbox"
                  checked={selectedLayer.animation?.translate?.enabled || false}
                  onChange={(e) => updateLayerAnimation(selectedLayer.id, 'translate', { enabled: e.target.checked })}
                />
                Translate
              </label>
            </div>
            
            {selectedLayer.animation?.translate?.enabled && (
              <div className={styles.animationProps}>
                {/* Direction Grid */}
                <div className={styles.directionGrid}>
                  {['up-left', 'up', 'up-right', 'left', null, 'right', 'down-left', 'down', 'down-right'].map((dir, i) => (
                    dir ? (
                      <button
                        key={dir}
                        className={`${styles.directionBtn} ${selectedLayer.animation?.translate?.direction === dir ? styles.active : ''}`}
                        onClick={() => updateLayerAnimation(selectedLayer.id, 'translate', { direction: dir })}
                      >
                        {DIRECTIONS.find(d => d.value === dir)?.icon}
                      </button>
                    ) : (
                      <div key={i} className={styles.directionCenter} />
                    )
                  ))}
                </div>
                
                <div className={styles.propRow}>
                  <label>Speed: {selectedLayer.animation.translate.speed}px/s</label>
                  <input
                    type="range"
                    min={10}
                    max={200}
                    value={selectedLayer.animation.translate.speed}
                    onChange={(e) => updateLayerAnimation(selectedLayer.id, 'translate', { speed: parseInt(e.target.value) })}
                  />
                </div>
                
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={selectedLayer.animation.translate.loop}
                    onChange={(e) => updateLayerAnimation(selectedLayer.id, 'translate', { loop: e.target.checked })}
                  />
                  Loop
                </label>
              </div>
            )}
          </div>
          
          {/* Zoom Animation */}
          <div className={styles.animationGroup}>
            <div className={styles.animationHeader}>
              <label>
                <input
                  type="checkbox"
                  checked={selectedLayer.animation?.zoom?.enabled || false}
                  onChange={(e) => updateLayerAnimation(selectedLayer.id, 'zoom', { enabled: e.target.checked })}
                />
                Zoom
              </label>
            </div>
            
            {selectedLayer.animation?.zoom?.enabled && (
              <div className={styles.animationProps}>
                <div className={styles.propRow}>
                  <label>Scale: {Math.round((selectedLayer.animation.zoom.scale || 0) * 100)}%</label>
                  <input
                    type="range"
                    min={-0.5}
                    max={0.5}
                    step={0.01}
                    value={selectedLayer.animation.zoom.scale || 0}
                    onChange={(e) => updateLayerAnimation(selectedLayer.id, 'zoom', { scale: parseFloat(e.target.value) })}
                  />
                </div>
                
                <div className={styles.propRow}>
                  <label>Duration: {(selectedLayer.animation.zoom.duration || 2000) / 1000}s</label>
                  <input
                    type="range"
                    min={500}
                    max={10000}
                    step={100}
                    value={selectedLayer.animation.zoom.duration || 2000}
                    onChange={(e) => updateLayerAnimation(selectedLayer.id, 'zoom', { duration: parseInt(e.target.value) })}
                  />
                </div>
                
                <div className={styles.propRow}>
                  <label>Easing</label>
                  <select
                    value={selectedLayer.animation.zoom.ease || 'power1.inOut'}
                    onChange={(e) => updateLayerAnimation(selectedLayer.id, 'zoom', { ease: e.target.value })}
                  >
                    {EASING_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                
                <div className={styles.checkboxRow}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={selectedLayer.animation.zoom.yoyo}
                      onChange={(e) => updateLayerAnimation(selectedLayer.id, 'zoom', { yoyo: e.target.checked })}
                    />
                    Yoyo
                  </label>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={selectedLayer.animation.zoom.loop}
                      onChange={(e) => updateLayerAnimation(selectedLayer.id, 'zoom', { loop: e.target.checked })}
                    />
                    Loop
                  </label>
                </div>
              </div>
            )}
          </div>
          
          {/* Rotate Animation */}
          <div className={styles.animationGroup}>
            <div className={styles.animationHeader}>
              <label>
                <input
                  type="checkbox"
                  checked={selectedLayer.animation?.rotate?.enabled || false}
                  onChange={(e) => updateLayerAnimation(selectedLayer.id, 'rotate', { enabled: e.target.checked })}
                />
                Rotate
              </label>
            </div>
            
            {selectedLayer.animation?.rotate?.enabled && (
              <div className={styles.animationProps}>
                <div className={styles.propRow}>
                  <label>Angle: {selectedLayer.animation.rotate.angle || 0}°</label>
                  <input
                    type="range"
                    min={-360}
                    max={360}
                    value={selectedLayer.animation.rotate.angle || 0}
                    onChange={(e) => updateLayerAnimation(selectedLayer.id, 'rotate', { angle: parseInt(e.target.value) })}
                  />
                </div>
                
                <div className={styles.propRow}>
                  <label>Duration: {(selectedLayer.animation.rotate.duration || 2000) / 1000}s</label>
                  <input
                    type="range"
                    min={500}
                    max={10000}
                    step={100}
                    value={selectedLayer.animation.rotate.duration || 2000}
                    onChange={(e) => updateLayerAnimation(selectedLayer.id, 'rotate', { duration: parseInt(e.target.value) })}
                  />
                </div>
                
                <div className={styles.propRow}>
                  <label>Easing</label>
                  <select
                    value={selectedLayer.animation.rotate.ease || 'power1.inOut'}
                    onChange={(e) => updateLayerAnimation(selectedLayer.id, 'rotate', { ease: e.target.value })}
                  >
                    {EASING_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                
                <div className={styles.checkboxRow}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={selectedLayer.animation.rotate.yoyo}
                      onChange={(e) => updateLayerAnimation(selectedLayer.id, 'rotate', { yoyo: e.target.checked })}
                    />
                    Yoyo
                  </label>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={selectedLayer.animation.rotate.loop}
                      onChange={(e) => updateLayerAnimation(selectedLayer.id, 'rotate', { loop: e.target.checked })}
                    />
                    Loop
                  </label>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Footer */}
      <div className={styles.footer}>
        <button className={styles.cancelBtn} onClick={onClose}>
          Cancel
        </button>
        <button className={styles.saveBtn} onClick={handleSave} disabled={!!nameError}>
          Save
        </button>
      </div>
    </div>
  );
};

export default BackgroundEditor;
